import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { BrowserCheckService } from '../services/browser-check.service';
import { AdminManageService } from '../services/adminService/admin-manage.service';
import { LoaderService } from '../services/loader.service';
import { PermissionCacheService } from '../services/permission-cache.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RouteProtectionGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly browserCheck: BrowserCheckService,
    private readonly adminManageService: AdminManageService,
    private readonly loaderService: LoaderService,
    private readonly permissionCache: PermissionCacheService
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const currentUrl = state.url;
    const userEmail = this.browserCheck.getSessionStorage('UserEmail') || '';

    // If no user email, deny access
    if (!userEmail) {
      this.router.navigate(['/access-deny-user']);
      return of(false);
    }

    // Check if route requires protection
    const protectedRoutes = [
      '/admin/manage-user',
      '/admin/manage-request',
      '/admin/edit-request',
      '/admin/company-code',
      '/admin/purchasing-code',
      '/buyer/request',
      '/buyer/other-request',
      '/lead-buyer/request',
      '/lead-buyer/other-request',
    ];

    if (
      !protectedRoutes.some((protectedRoute) =>
        currentUrl.startsWith(protectedRoute)
      )
    ) {
      return of(true); // Allow access to non-protected routes
    }

    // Check if we have cached permissions first
    const cachedPermissions = this.permissionCache.getCachedPermissions();

    // If we have recent cached permissions, use them
    if (cachedPermissions.isValid) {
      const hasAccess = this.validateRouteAccess(
        currentUrl,
        cachedPermissions.sysAdmin,
        cachedPermissions.lead,
        cachedPermissions.buyer
      );
      if (!hasAccess) {
        this.router.navigate(['/access-deny-user']);
        return of(false);
      }
      return of(true);
    }

    // For protected routes without valid cache, show loader and check permissions
    this.loaderService.showLoader();

    return this.adminManageService.getUserDetails(userEmail).pipe(
      map((response) => {
        this.loaderService.hideLoader();
        const userData = response.body;

        if (!userData) {
          this.router.navigate(['/access-deny-user']);
          return false;
        }

        // Cache the permissions
        this.permissionCache.cachePermissions(
          userData.userTypeSysAdmin || '',
          userData.userTypeLead || '',
          userData.userTypeBuyer || ''
        );

        const hasAccess = this.validateRouteAccess(
          currentUrl,
          userData.userTypeSysAdmin || '',
          userData.userTypeLead || '',
          userData.userTypeBuyer || ''
        );

        if (!hasAccess) {
          this.router.navigate(['/access-deny-user']);
          return false;
        }

        return true;
      }),
      catchError(() => {
        this.loaderService.hideLoader();
        this.permissionCache.clearCache();
        this.router.navigate(['/access-deny-user']);
        return of(false);
      })
    );
  }

  private validateRouteAccess(
    url: string,
    sysAdmin: string,
    lead: string,
    buyer: string
  ): boolean {
    const adminRoutes = [
      '/admin/manage-user',
      '/admin/manage-request',
      '/admin/edit-request',
      '/admin/company-code',
      '/admin/purchasing-code',
    ];
    const buyerRoutes = ['/buyer/request', '/buyer/other-request'];
    const leadBuyerRoutes = [
      '/lead-buyer/request',
      '/lead-buyer/other-request',
    ];

    // Admin route access check
    if (adminRoutes.some((route) => url.startsWith(route))) {
      if (sysAdmin !== '1') {
        return false;
      }
      return true;
    }

    // Buyer route access check
    if (buyerRoutes.some((route) => url.startsWith(route))) {
      if (buyer !== '1') {
        return false;
      }
      return true;
    }

    // Lead Buyer route access check
    if (leadBuyerRoutes.some((route) => url.startsWith(route))) {
      if (lead !== '1') {
        return false;
      }
      return true;
    }

    return true;
  }
}
